import { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useI18n } from '@/hooks/useI18n';
import { DEFAULT_REMINDER_OFFSETS } from '@/lib/builder/models/payment-plan.model';
import { toggleReminderOffset } from '@/lib/builder/utils/payment-plan.utils';

interface ReminderOffsetsFieldProps {
    value: number[];
    onChange: (offsets: number[]) => void;
    disabled?: boolean;
}

/**
 * Promemoria di una rata, espressi in giorni PRIMA della scadenza (quindi negativi).
 * Non esistono solleciti dopo la scadenza: e' una decisione di prodotto, non un limite
 * tecnico, per questo l'input libero accetta solo numeri positivi e li converte in
 * "giorni prima".
 */
export const ReminderOffsetsField = ({ value, onChange, disabled }: ReminderOffsetsFieldProps) => {
    const { t } = useI18n();
    const [customDays, setCustomDays] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const addCustom = () => {
        const days = parseInt(customDays, 10);
        if (!Number.isFinite(days) || days <= 0) return;
        onChange(toggleReminderOffset(value, -Math.abs(days)));
        setCustomDays('');
        setIsOpen(false);
    };

    return (
        <div className="flex flex-wrap items-center gap-1">
            {value.map((offset) => (
                <span
                    key={offset}
                    className="inline-flex items-center gap-0.5 rounded-full border border-gray-200 bg-gray-50 pl-1.5 pr-0.5 py-px text-[11px] leading-4 text-gray-700 tabular-nums whitespace-nowrap"
                >
                    {t('checkout.paymentPlan.reminders.daysBefore', { days: Math.abs(offset) })}
                    {!disabled && (
                        <button
                            type="button"
                            onClick={() => onChange(toggleReminderOffset(value, offset))}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            aria-label={t('checkout.paymentPlan.reminders.remove')}
                        >
                            <X className="w-2.5 h-2.5" />
                        </button>
                    )}
                </span>
            ))}

            {value.length === 0 && (
                <span className="text-[11px] italic text-gray-400">
                    {t('checkout.paymentPlan.reminders.none')}
                </span>
            )}

            {!disabled && (
                <Popover open={isOpen} onOpenChange={setIsOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-5 px-1.5 text-[11px] leading-4 text-gray-500 border border-dashed border-gray-300"
                        >
                            <Plus className="w-2.5 h-2.5 mr-0.5" />
                            {t('checkout.paymentPlan.reminders.add')}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-3" align="start">
                        <div className="flex flex-col gap-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                {t('checkout.paymentPlan.reminders.presets')}
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                                {DEFAULT_REMINDER_OFFSETS.map((offset) => (
                                    <Button
                                        key={offset}
                                        type="button"
                                        variant={value.includes(offset) ? 'default' : 'outline'}
                                        size="sm"
                                        className="h-7 px-2 text-xs tabular-nums"
                                        onClick={() => onChange(toggleReminderOffset(value, offset))}
                                    >
                                        {t('checkout.paymentPlan.reminders.daysBefore', { days: Math.abs(offset) })}
                                    </Button>
                                ))}
                            </div>

                            <div className="flex items-center gap-2 pt-1">
                                <Input
                                    type="number"
                                    min={1}
                                    value={customDays}
                                    onChange={(e) => setCustomDays(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && addCustom()}
                                    placeholder={t('checkout.paymentPlan.reminders.customPlaceholder')}
                                    className="h-8 text-xs"
                                />
                                <Button type="button" size="sm" className="h-8" onClick={addCustom}>
                                    {t('common.add')}
                                </Button>
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>
            )}
        </div>
    );
};
